/**
 * Session Selector Component
 *
 * Horizontal card-based session selector using Equal DS CardTabs.
 * Displays all available data sessions with their statuses and account counts.
 */

import React from 'react';
import { CardTabs } from 'equal-ds-ui';
import type { TransformedDataSession } from '../../../types/consent';

interface SessionSelectorProps {
  sessions: TransformedDataSession[];
  selectedSessionId: string;
  onSessionSelect: (sessionId: string) => void;
}

export const SessionSelector: React.FC<SessionSelectorProps> = ({
  sessions,
  selectedSessionId,
  onSessionSelect,
}) => {
  // Transform DataSession[] to CardTabs format
  const sessionTabs = sessions.map((session) => {
    // Map our session status to CardTabs status format
    let cardStatus: 'active' | 'failed' | 'data-ready' | 'partial-data' | undefined;

    // Normalize status to uppercase for comparison
    const statusUpper = (session.status || '').toUpperCase();

    // Map various status values to CardTabs expected format
    if (statusUpper === 'ACTIVE') {
      cardStatus = 'active';
    } else if (statusUpper === 'FAILED' || statusUpper === 'TIMEOUT' || statusUpper === 'ERROR') {
      cardStatus = 'failed';
    } else if (statusUpper === 'COMPLETE' || statusUpper === 'COMPLETED' || statusUpper === 'READY' || statusUpper === 'DELIVERED' || statusUpper === 'DATA READY') {
      // COMPLETE/COMPLETED/READY/DELIVERED should show as green (success) - use 'active' which is green in CardTabs
      cardStatus = 'active';
    } else if (statusUpper === 'PARTIAL' || statusUpper === 'PARTIAL DATA' || statusUpper === 'PARTIAL-DATA') {
      cardStatus = 'partial-data';
    }

    // Truncate session ID for display
    const label =
      session.sessionId.length > 15
        ? session.sessionId.substring(0, 15) + '...'
        : session.sessionId;

    // Show account count as metadata
    const accountCount = session.accounts.length;
    const metadata = `${accountCount} account${accountCount !== 1 ? 's' : ''}`;

    return {
      id: session.sessionId,
      label,
      status: cardStatus,
      metadata,
    };
  });

  return (
    <div className="overflow-x-auto pb-2">
      <CardTabs tabs={sessionTabs} activeTab={selectedSessionId} onTabChange={onSessionSelect} />
    </div>
  );
};
